import React, { useCallback, useState } from 'react'
import { Popover } from '@headlessui/react'
import classNames from 'classnames'
import { When } from 'react-if'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { CaptionMD, ParagraphMD, ParagraphSM, TitleMD, TitleSM, TitleXS } from '@/atoms/Text'
import { Offer } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { formatPrice } from '@/services/PriceFormatter'
import { Project } from '@/services/ProjectsService'
import { useLocale } from '@/utils/LocaleUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface TheatricalExpressOffersHeaderProps {
  projectName: Project['name']
  selectedOffer: Offer
  shouldShowPeopleImpacted?: boolean
}

export const TheatricalExpressOffersHeader: React.FC<TheatricalExpressOffersHeaderProps> = ({
  projectName,
  selectedOffer,
  shouldShowPeopleImpacted = false,
}) => {
  const { t } = useTranslate('common')
  const { locale } = useLocale()
  const [isHovering, setIsHovering] = useState(false)
  const selectedOfferPrice =
    selectedOffer &&
    formatPrice(selectedOffer?.price, {
      includeDecimals: false,
      currency: selectedOffer?.currency,
    })
  const getReach = useCallback(
    (offer: Offer) => {
      const num = getPeopleImpacted(offer)
      const peopleImpacted = new Intl.NumberFormat(locale, { notation: 'compact' }).format(num)
      return t('ticketsForOffer', {
        count: num,
        peopleImpacted,
        defaultValue_one: '1 Gifted Ticket',
        defaultValue_other: '{{peopleImpacted}} Gifted Tickets',
      })
    },
    [t, locale],
  )
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-200">
            <PayItForwardOutlineIcon size={24} color="black" />
          </div>
          <div className="flex flex-col justify-center">
            <TitleSM color="gray-950" weight="bold">
              {t('payItForwardv2', 'Pay it Forward')}
            </TitleSM>
            <CaptionMD color="gray-600">{projectName}</CaptionMD>
          </div>
        </div>
        {selectedOfferPrice && (
          <TitleMD weight="bold" className="self-start">
            {selectedOfferPrice}
          </TitleMD>
        )}
      </div>
      <When condition={shouldShowPeopleImpacted}>
        <div className={'row flex'}>
          <TitleMD weight="bold">{getReach(selectedOffer)}</TitleMD>
          <Popover className={classNames('relative !z-50')}>
            {({ open }) => {
              return (
                <>
                  <Popover.Button
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    onClick={() => setIsHovering(false)}
                    className={classNames('flex relative')}
                  >
                    <CircleInfoIcon size={20} color={'gray-500'} className={'ml-2'} />
                  </Popover.Button>
                  {(open || isHovering) && (
                    <Popover.Panel
                      static
                      className={classNames(
                        'absolute w-[320px] mt-3 p-6 !z-50 text-left bg-white shadow-light-2 rounded-2xl -left-32',
                      )}
                    >
                      <div className={classNames('absolute -top-3 h-6 w-6 rotate-45 bg-white')} />
                      <TitleXS color="core-gray-950" weight="bold">
                        {t('whatIsPayItForwardv2', 'What is Pay it Forward?')}
                      </TitleXS>
                      <ParagraphSM className="mt-2 text-[0.688rem] leading-[16px]" color="core-gray-700">
                        {t(
                          'PIFMissionDescriptionV3',
                          `Support our mission to share {{projectName}} with everyone. By purchasing tickets through our 'Pay it Forward' program, you're enabling Angel Studios to gift movie tickets to fans who may have been unable to get a ticket. Every ticket you purchase here goes directly to someone eager to experience the movie but unable to buy a ticket themselves.`,
                          { projectName },
                        )}
                      </ParagraphSM>
                    </Popover.Panel>
                  )}
                </>
              )
            }}
          </Popover>
        </div>
        <ParagraphMD className="mt-1 text-sm" color="core-gray-600" weight="medium">
          <Translate t={t} i18nKey="payItForwardActiveOfferSubBehalf">
            Select the number of tickets you would like Angel Studios to gift on your behalf:
          </Translate>
        </ParagraphMD>
      </When>
    </div>
  )
}
